/* eslint-disable */
import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { SessionContext } from "../../../../context/sessionContext";
import Logger from "../../../../logger";
import Loader from "../../common/loader/loader";
import BannerNotification from "../../common/banner/bannerNotification";
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';
import Checkbox from "../../common/checkbox/checkbox";
import { StepLayout } from "../../common/steplayout/StepLayout";
import { useMFAVerify } from './useMFAVerify';
import CustomLink from "../../common/link/link";
import CodeInput from './CodeInput';
import { useMyVWQueryParams } from '../../../hooks/useMyVWQueryParams';
import CountdownTimer from "../../common/countdownTimer/countdownTimer";

interface EnterEmailCodeProps {
  onChangeMethod: () => void;
  onSendCode: () => Promise<void>;
  triggerError?: string | null;
  triggerAccountLocked?: boolean;
  maskedDestination?: string;
  triggerLoading?: boolean;
}

const EnterEmailCode: React.FC<EnterEmailCodeProps> = ({ onChangeMethod, onSendCode, triggerError, triggerAccountLocked = false, maskedDestination, triggerLoading = false }) => {
  const { sessionDetails } = useContext(SessionContext)!;
  const { buildSearch } = useMyVWQueryParams();
  const [isExpired, setIsExpired] = useState(false);
  const [reset, setReset] = useState(false);
  const {
    code,
    isError,
    accountLocked,
    failedAttempts,
    submitPending,
    rememberDevice,
    inputRef,
    handleCodeChange,
    handleVerify,
    resetVerifyState,
    setRememberDevice,
  } = useMFAVerify('EnterEmailCode');

  const handleSendNewCode = async () => {
    resetVerifyState();
    handleCodeChange('');
    setReset(prev => !prev);
    setIsExpired(false);
    await onSendCode();
  };

  const isLocked = accountLocked || triggerAccountLocked;
  const isBusy = submitPending || triggerLoading;
  const isInteractionDisabled = isBusy || isLocked || isExpired;
  const showTriggerError = !!triggerError && !isLocked;
  const showVerifyError = isError && !isLocked && !showTriggerError;
  const showWarningBanner = failedAttempts >= 2 && !isLocked;

  return (
    <StepLayout
      title={<h3 className='u-mb-small'>{i18n.t(rk.TITLE_MYVW_MFA_VERIFY)}</h3>}
    >
      <Loader isLoading={isBusy} />
      {isLocked && (
        <div className="u-mt" data-testid="mfa-locked-banner">
          <BannerNotification notificationStyle="error" title={i18n.t(rk.TITLE_LOCKOUT_ERROR)}>
            {i18n.t(rk.TEXT_LOCKOUT_ERROR_MFA) as any}
          </BannerNotification>
        </div>
      )}
      {showTriggerError && (
        <div className="u-mt" data-testid="trigger-error-banner">
          <BannerNotification notificationStyle="error">
            {triggerError as any}
          </BannerNotification>
        </div>
      )}
      {showWarningBanner && (
        <div className="u-mt" data-testid="mfa-warning-banner">
          <BannerNotification
            title={`${i18n.t(rk.TITLE_LOCKOUT_WARNING1)} ${5 - failedAttempts}
             ${failedAttempts === 4 ? i18n.t(rk.TITLE_LOCKOUT_WARNING3) : i18n.t(rk.TITLE_LOCKOUT_WARNING2)}`}
            notificationStyle="warning"
          >
            {i18n.t(rk.TEXT_LOCKOUT_WARNING_VERIFY) as any}
          </BannerNotification>
        </div>
      )}
      <p className="u-mt" data-testid="timer-message">
        {i18n.t(rk.TEXT_MYVW_MFA_VERIFY_EMAIL, { destination: maskedDestination })}{' '}
        {i18n.t(rk.TEXT_MYVW_MFA_CODE_EXPIRES_IN)}{' '}
        <CountdownTimer minutes={15} seconds={0} onFinish={() => setIsExpired(true)} reset={reset} /> {i18n.t(rk.TEXT_MYVW_MINUTES)}
      </p>
      <div className="u-mb-small">
        <Link
          data-testid="update-email-link"
          to={'/myVW/update-email' + buildSearch()}
          aria-label="Update email address"
        >
          {i18n.t(rk.LINK_MYVW_MFA_UPDATE_EMAIL)}
        </Link>
      </div>
      <CodeInput
        key={`mfa-code-input-${reset ? '1' : '0'}`}
        value={code}
        onChange={handleCodeChange}
        disabled={isInteractionDisabled}
        inputRef={inputRef}
      />
      {(isExpired || showVerifyError) && !isLocked && !showTriggerError && (
          <p
            className="c-error-message"
            style={{ fontSize: "12px", marginLeft: "0px" }}
          >
            
              {i18n.t(rk.ERROR_INVALID_MFA_CODE)}
          </p>
        )}
      <p className="u-mt-xsmall">
        <span>{i18n.t(rk.TEXT_DIDNT_RECV_CODE)} </span>
        <CustomLink
          value={i18n.t(rk.LINK_SEND_NEW_CODE)}
          ariaLabel="Send a new code"
          testid="send-new-code-link"
          fontSize="14px"
          onClick={handleSendNewCode}
        />
      </p>
      <div className="u-mv-large">
        <Checkbox
          disabled={isInteractionDisabled}
          label={i18n.t(rk.REMEMBER_DEVICE)}
          id="rememberDevice"
          onChange={() => {
            setRememberDevice(prev => {
              Logger.info(
                `${!prev ? 'Checked' : 'Unchecked'} remember this device checkbox`,
                'Location ==> myVWMfa [EnterEmailCode]',
                sessionDetails
              );
              return !prev;
            });
          }}
          checked={rememberDevice}
          testid="remember-device-checkbox"
        />
      </div>
      <div className="o-button-container o-button-container--center u-mt-xlarge">
        <button
          className="c-btn c-btn--primary o-button-container__button"
          onClick={handleVerify}
          disabled={code.length < 6 || isInteractionDisabled}
          data-testid="app-mfa-verify"
        >
          <span className="c-btn__text">{i18n.t(rk.BUTTON_VERIFY)}</span>
        </button>
      </div>
      <div className="o-button-container o-button-container--center u-mt-xlarge">
        <button
          className="c-link o-button-container__button--center"
          onClick={onChangeMethod}
          disabled={submitPending}
          data-testid="app-mfa-change-method"
        >
          <span className="c-btn__text">{i18n.t(rk.LINK_CHANGE_VERIFICATION_METHOD)}</span>
        </button>
      </div>
    </StepLayout>
  );
};

export default EnterEmailCode;
